import { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../component/b2b/navBar/b2bNavBar'
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { useForm } from 'react-hook-form';
import { IoMdArrowRoundBack } from "react-icons/io";
import {
    FaCalculator, FaUser, FaShoppingCart, FaPlus, FaBox, FaTrash, FaShieldAlt,
    FaWarehouse, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity,
    FaFlag, FaRupeeSign, FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen,
    FaRulerCombined, FaFileInvoice, FaCloudUploadAlt, FaFileAlt, FaSearch
} from 'react-icons/fa';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import B2BViewRateForword from '../../../component/b2b/models/priceEstimatorModelB2b';
import { Base_Url, b2bAddShipment, getB2bpIncode, b2bWarehouseListAll, } from '../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import axios from 'axios';
import B2BUploadBulk from '../../../component/b2b/models/uploadBulkModel';
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const schema = z.object({
    warehouse: z.string().optional(),
    firstName: z.string().min(1, "First Name is required").regex(/^[a-zA-Z\s]+$/, "First Name must only contain alphabetic characters"),
    lastName: z.string().min(1, "Last Name is required").regex(/^[a-zA-Z\s]+$/, "Last Name must only contain alphabetic characters"),
    email: z.string().email("Invalid email format").optional().or(z.literal("")),
    mobile: z.string()
        .length(10, "Mobile number must be exactly 10 digits")
        .regex(/^\d+$/, "Mobile number must be numeric"),
    address: z
        .string()
        .trim()
        .min(10, "Full Address must be at least 10 characters")
        .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)")
        .refine((val) => val.trim().length >= 10, {
            message: "Address cannot be only spaces",
        }),
    pincode: z.string()
        .length(6, "Pincode should be exactly 6 characters")
        .regex(/^\d+$/, "Pincode must be numeric"),
    city: z.string().min(1, "City is required").regex(/^[a-zA-Z\s]+$/, "City must only contain alphabetic characters"),
    state: z.string().min(1, "State is required").regex(/^[a-zA-Z\s]+$/, "State must only contain alphabetic characters"),
    orderId: z.string().min(1, "Order ID is required"),
    invoiceAmount: z.string()
        .min(1, "Invoice Amount is required")
        .regex(/^\d+(\.\d+)?$/, "Invoice Amount must be numeric"),
    invoiceNumber: z.string()
        .min(1, "Invoice number is required")
        .regex(/^[A-Za-z0-9\/-]*$/, "Invoice number must only contain letters, numbers, / or -"),
    eWaybillNo: z.string()
        .max(12, "E-Waybill number must be at most 12 characters")
        .optional(),
    paymentMode: z.string().min(1, "Payment Mode is required"),
    productDes: z.string().min(1, "Product Description is required"),
    codAmount: z.string().optional(),
    insurence: z.string().optional(),
    total_weight: z.string()
        .min(1, "Total Weight is required")
        .regex(/^\d*\.?\d+$/, "Total Weight must be a positive number and can be a float"),
    length: z.string()
        .regex(/^\d*\.?\d+$/, "Length must be a positive number and can be a float")
        .optional(),
    width: z.string()
        .regex(/^\d*\.?\d+$/, "Width must be a positive number and can be a float")
        .optional(),
    height: z.string()
        .regex(/^\d*\.?\d+$/, "Height must be a positive number and can be a float")
        .optional(),
}).refine(
    (data) => {
        const amount = parseFloat(data.invoiceAmount);
        if (amount > 50000) {
            return !!data.eWaybillNo && data.eWaybillNo.trim() !== "";
        }
        return true;
    },
    {
        message: "E-Waybill number is required when Invoice Amount is greater than 50000",
        path: ["eWaybillNo"],
    }
);

// ---------- visual-only helper components (B2C design language) ----------
const errorText = (msg) => (
    <p className="text-red-500 text-[8px] mt-0.5 font-medium flex items-center gap-1">
        <span className="w-0.5 h-0.5 rounded-full bg-red-500 inline-block" />
        {msg}
    </p>
);

const Field = ({ icon: Icon, label, error, children, htmlFor, isDarkMode }) => (
    <div>
        {label && (
            <label htmlFor={htmlFor} className={`block text-[10px] font-medium tracking-wider mb-0.5 ${isDarkMode ? "text-gray-600" : "text-gray-700"}`}>
                {label}
            </label>
        )}
        <div className="relative group">
            {Icon && (
                <Icon
                    size={10}
                    className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-gray-500 group-focus-within:text-white" : "text-gray-400 group-focus-within:text-black"}`}
                />
            )}
            {children}
        </div>
        {error && errorText(error)}
    </div>
);

const SectionCard = ({ number, icon: Icon, title, subtitle, children, className = "", isDarkMode }) => (
    <div
        className={`relative overflow-hidden rounded-lg border p-2.5 transition-colors
            ${isDarkMode
                ? "bg-gray-300/50 border-gray-700"
                : "bg-gray-300/10 border-gray-300"
            } ${className}`}
    >
        <div className="relative flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
                <span className={`flex items-center justify-center w-6 h-6 rounded-lg shrink-0 border
                    ${isDarkMode
                        ? "bg-blue-600 text-white border-gray-600"
                        : "bg-blue-600 text-white border-gray-300"
                    }`}>
                    <Icon size={11} />
                </span>
                <div>
                    <h2 className={`text-[12px] font-bold tracking-wide ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
        <div className="relative">{children}</div>
    </div>
);

// Searchable Warehouse Dropdown Component (logic unchanged, restyled to match B2C inputs)
const SearchableWarehouseSelect = ({
    value,
    onChange,
    options,
    isDarkMode,
    error,
    label,
    placeholder = "Search warehouse..."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption ? selectedOption.label : '';

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    };

    const inputBase = `w-full pl-7 pr-2 py-1.5 text-[10px] rounded-md border transition-all duration-200 outline-none
        placeholder:text-gray-400
        ${isDarkMode
            ? "bg-gray-900/50 border-gray-700 text-white placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
            : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
        }`;

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="relative group">
                <FaWarehouse
                    size={10}
                    className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-gray-500 group-focus-within:text-white" : "text-gray-400 group-focus-within:text-black"}`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={(e) => {
                        if (isOpen) {
                            setSearchTerm(e.target.value);
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={handleInputClick}
                    placeholder={placeholder}
                    className={inputBase}
                />
                <button
                    type="button"
                    onClick={handleInputClick}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 transition-colors ${isDarkMode ? "text-gray-500 hover:text-white" : "text-gray-400 hover:text-black"}`}
                >
                    <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div
                    className={`absolute z-50 w-full mt-0.5 max-h-40 overflow-y-auto rounded-md border shadow-lg
                        ${isDarkMode
                            ? "bg-black/95 border-gray-700"
                            : "bg-white border-gray-300"
                        }`}
                >
                    {filteredOptions.length === 0 ? (
                        <div className={`px-3 py-2 text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                            No warehouses found
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`px-3 py-1.5 text-[10px] cursor-pointer transition-colors flex items-center justify-between
                                    ${option.value === value
                                        ? (isDarkMode ? "bg-blue-600/30 text-white" : "bg-blue-50 text-black")
                                        : (isDarkMode ? "hover:bg-gray-800 text-white" : "hover:bg-gray-100 text-black")
                                    }`}
                            >
                                <div className="flex flex-col">
                                    <span className="font-medium">{option.label}</span>
                                    <span className={`text-[8px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                                        ID: {option.value}
                                    </span>
                                </div>
                                {option.pincode && (
                                    <span className={`text-[8px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                                        PIN: {option.pincode}
                                    </span>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
            {error && errorText(error)}
        </div>
    );
};

export default function B2BForwordShipment() {
    const [invoiceFileName, setInvoiceFileName] = useState('');
    const [secondaryFile, setSecondaryFile] = useState('');
    const { isDarkMode, isToggleSidebar, selectedCft } = useMyContext()
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [uploadBulkPopupOpen, setUploadBulkPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const Navigation = useNavigate();
    const [wareHouseList, setwareHouseList] = useState([])
    const [wareHouseId, setwareHouseId] = useState('')
    const [wareHousePincode, setwareHousePincode] = useState('');
    const [reqBody, setReqBody] = useState([]);
    const [totalBoxes, setTotalBoxes] = useState(0);
    const [selectedUnit, setSelectedUnit] = useState('cm');
    const [boxSets, setBoxSets] = useState([]);
    const [remainingBoxes, setRemainingBoxes] = useState(0);
    const [file, setFile] = useState(null);
    const [sec_file, set_Sec_File] = useState(null);
    const [lrnOption, setLrnOption] = useState('auto');
    const [manualLrn, setManualLrn] = useState('');
    const [phoneResults, setPhoneResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingPhone, setLoadingPhone] = useState(false);
    const [searchText, setSearchText] = useState("");

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const { register, handleSubmit, formState: { errors }, reset, trigger, watch, setValue } = useForm({
        resolver: zodResolver(schema),
    });

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };
    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const UploadBulkViewClick = () => {
        setUploadBulkPopupOpen(true);
    }

    const handleCloseUploadBulk = () => {
        setUploadBulkPopupOpen(false);
    };

    const pincode = watch("pincode");

    useEffect(() => {
        if (pincode && pincode.length === 6) {
            fetchPincodeData(pincode);
        }
    }, [pincode]);

    const fetchPincodeData = async (pincode) => {
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getB2bpIncode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: {
                    'token': token,
                    'cus_id': cus_id,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data;
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
            } else if (data && data.message === "Unauthorized access - Invalid token") {
                toast.error(data.message + " Please login Again");
            } else if (data && data.status === "FAIL") {
                toast.error("Something Went Wrong Please Try Again With Another Pincode");
            } else {
                toast.error("Failed to fetch city and state data");
            }
        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
        }
    };

    const paymentMode = watch("paymentMode");

    useEffect(() => {
        if (paymentMode === "prepaid") {
            setValue("codAmount", "0");
        } else if (paymentMode === "cod") {
            setValue("codAmount", "");
        }
    }, [paymentMode, setValue]);

    const handleInputChange = (event) => {
        const { value, id } = event.target;
        if (['pincode', 'mobile', 'boxQty'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
        }
        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    useEffect(() => {
        fetchWareHouseList()
    }, [])

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({
            'token': token,
            'cus_id': cusid,
            'Content-Type': 'application/json'
        });

        try {
            const response = await fetch(Base_Url + b2bWarehouseListAll, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.warehouse_id,
                label: warehouse.name,
                pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData)
        } catch (error) {
            console.error('Error:', error);
        }
    }

    const handleNaviagtion = () => {
        Navigation("/b2b/dashboard");
    }

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        const invoiceAmount = parseFloat(data.invoiceAmount) || 0;

        if (boxSets && boxSets.length > 0) {
            const invalidBoxSet = boxSets.find(box =>
                !box.quantity || !box.length || !box.width || !box.height ||
                box.quantity <= 0 || box.length <= 0 || box.width <= 0 || box.height <= 0
            );

            if (invalidBoxSet) {
                toast.error("Please fill all box set dimensions with positive values");
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const formData = new FormData();

            formData.append('warehouse_id', wareHouseId || '');
            formData.append('type', selectedCft || '');
            formData.append('total_boxes', totalBoxes || '');
            formData.append('payment_mode', data.paymentMode || '');
            formData.append('cod_amount', data.codAmount || '0');
            formData.append('total_weight', data.total_weight || '');

            const dropoffLocation = {
                consignee_name: `${data.firstName} ${data.lastName}`,
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                zip: data.pincode || '',
                phone: data.mobile || '',
                email: data.email || '',
            };
            formData.append('dropoff_location', JSON.stringify(dropoffLocation));

            const invoices = [{
                ewaybill: data.eWaybillNo,
                inv_num: data.invoiceNumber,
                inv_amt: data.invoiceAmount,
                inv_qr_code: ""
            }];
            formData.append('invoices', JSON.stringify(invoices));

            const shipmentDetails = [{
                order_id: data.orderId || '',
                box_count: totalBoxes || 1,
                description: data.productDes || '',
                weight: parseFloat(data.total_weight) || 0,
                waybills: [],
                master: "False"
            }];
            formData.append('shipment_details', JSON.stringify(shipmentDetails));

            const dimensions = boxSets && boxSets.length > 0
                ? boxSets.map(boxSet => ({
                    box_count: parseInt(boxSet.quantity) || 1,
                    length: parseFloat(boxSet.length) || 0,
                    width: parseFloat(boxSet.width) || 0,
                    height: parseFloat(boxSet.height) || 0
                }))
                : [{
                    box_count: 1,
                    length: parseFloat(data.length) || 0,
                    width: parseFloat(data.width) || 0,
                    height: parseFloat(data.height) || 0
                }];
            formData.append('dimensions', JSON.stringify(dimensions));

            const docData = [{
                doc_type: "INVOICE_COPY",
                doc_meta: {
                    invoice_num: [data.orderId || '']
                }
            }];
            formData.append('doc_data', JSON.stringify(docData));

            if (file) formData.append('doc_file', file);
            if (sec_file) formData.append('sec_doc', sec_file);

            const billingAddress = {
                name: `${data.firstName} ${data.lastName}`,
                company: "pickupxpress",
                consignor: data.firstName || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                pin: data.pincode || '',
                phone: data.mobile || '',
                pan_number: "ABCDE1234F",
                gst_number: "09AAACH7409R1ZZ"
            };
            formData.append('billing_address', JSON.stringify(billingAddress));

            formData.append('insurence', data.insurence === 'carrierRisk' ? 'Carrier' : 'Owner');
            formData.append('fm_pickup', 'True');
            formData.append('retailer_id', '2');

            if (lrnOption === 'manual' && manualLrn) {
                formData.append('lrn', manualLrn);
            }

            const response = await axios.post(Base_Url + b2bAddShipment, formData, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                setTimeout(() => toast.success("Shipment added successfully ....!"), 1000);
                setTableData(response.data);
                setIsSubmitting(false);
                handleNaviagtion();
            } else {
                toast.error(response.data.message);
                setIsSubmitting(false);
            }
        } catch (error) {
            if (error.response) {
                const { data } = error.response;
                if (data.messages && typeof data.messages === 'object') {
                    Object.values(data.messages).forEach((msg) => toast.error(msg));
                } else if (data.message) {
                    toast.error(data.message);
                }
                setIsSubmitting(false);
            } else if (error.request) {
                toast.error("No response from server. Please check your network.");
                setIsSubmitting(false);
            } else {
                toast.error("An unexpected error occurred.");
                setIsSubmitting(false);
            }
        }
    };

    const onErrors = (errors) => {
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey]) {
            toast.error(errors[firstErrorKey].message);
        }
    };

    const handleTotalBoxesChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        setTotalBoxes(value);
        setRemainingBoxes(value);
        setBoxSets([]);
    };

    const handleAddBoxSet = () => {
        if (remainingBoxes > 0) {
            setBoxSets([...boxSets, { quantity: remainingBoxes, length: '', width: '', height: '' }]);
            setRemainingBoxes(0);
        }
    };

    const handleBoxSetChange = (index, field, value) => {
        const newBoxSets = [...boxSets];
        const updatedBoxSet = { ...newBoxSets[index] };

        if (field === 'quantity') {
            const newQuantity = parseInt(value) || 0;
            const otherBoxesQuantity = boxSets.reduce((sum, box, i) =>
                i !== index ? sum + (parseInt(box.quantity) || 0) : sum, 0
            );

            if (newQuantity + otherBoxesQuantity <= totalBoxes) {
                updatedBoxSet.quantity = newQuantity;
                newBoxSets[index] = updatedBoxSet;
                setBoxSets(newBoxSets);
                setRemainingBoxes(totalBoxes - (newQuantity + otherBoxesQuantity));
            }
        } else {
            updatedBoxSet[field] = value;
            newBoxSets[index] = updatedBoxSet;
            setBoxSets(newBoxSets);
        }
    };

    const handleDeleteBoxSet = (index) => {
        const deletedQuantity = boxSets[index].quantity || 0;
        const newBoxSets = boxSets.filter((_, i) => i !== index);
        setBoxSets(newBoxSets);
        const totalUsedBoxes = newBoxSets.reduce((sum, box) => sum + (box.quantity || 0), 0);
        setRemainingBoxes(totalBoxes - totalUsedBoxes);
    };

    const fetchByPhone = async (phone) => {
        try {
            setLoadingPhone(true);
            const res = await fetch(
                `https://api.pickupxpress.com/B2B/shipment/phonefilter?phone=${phone}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: localStorage.getItem("cus_id"),
                        token: localStorage.getItem("token"),
                    },
                }
            );

            const json = await res.json();
            if (json.status === "SUCCESS") {
                setPhoneResults(json.data || []);
                setShowDropdown(true);
            } else {
                setPhoneResults([]);
                setShowDropdown(false);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPhone(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const inputId = event.target.id;
            if (inputId === 'invoiceDocs') {
                setFile(selectedFile);
                setInvoiceFileName(selectedFile.name);
            } else if (inputId === 'secondaryDocs') {
                set_Sec_File(selectedFile);
                setSecondaryFile(selectedFile.name);
            }
        }
    };

    const handleSelectCustomer = (item) => {
        const [firstName, ...lastName] = item.consignee_name.split(" ");
        setValue("firstName", firstName || "");
        setValue("lastName", lastName.join(" ") || "");
        setValue("email", item.email || "");
        setValue("address", item.address || "");
        setValue("city", item.city || "");
        setValue("state", item.state || "");
        setValue("pincode", item.zip || "");
        setValue("mobile", item.phone || "");
        setShowDropdown(false);
    };

    const handleWarehouseSelect = (selectedValue) => {
        const selectedItem = wareHouseList.find((item) => item.value === selectedValue);
        if (selectedItem) {
            setwareHouseId(selectedItem.value);
            setwareHousePincode(selectedItem.pincode);
            setValue("warehouse", selectedValue);
            trigger("warehouse");
        }
    };

    const inputBase = `w-full pl-7 pr-2 py-1.5 text-[10px] rounded-md border transition-all duration-200 outline-none
        placeholder:text-gray-400
        ${isDarkMode
            ? "bg-gray-900/50 border-gray-700 text-white placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
            : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
        }`;

    const inputPlain = `w-full px-2.5 py-1.5 text-[10px] rounded-md border transition-all duration-200 outline-none
        placeholder:text-gray-400
        ${isDarkMode
            ? "bg-gray-900/50 border-gray-700 text-white placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
            : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
        }`;

    const labelBase = `block text-[10px] font-medium tracking-wider mb-0.5 ${isDarkMode ? "text-gray-600" : "text-gray-700"}`;

    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

    return (
        <>
            <div className={`h-screen flex flex-col overflow-hidden relative ${isDarkMode ? "bg-black" : "bg-gray-50"}`}>
                <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

                <NavBar />
                <div className="flex flex-1 overflow-hidden">
                    {isToggleSidebar && (
                        <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
                            <Sidebar />
                        </div>
                    )}

                    <div
                        className={`flex-1 overflow-auto relative bg-white dark:bg-black transition-all duration-300 custom-scrollbar ${isDarkMode ? "text-white" : "text-black"}`}
                    >
                        <div className="container mx-auto px-3 pt-3 pb-4 overflow-x-hidden relative">

                            {/* PAGE HEADER */}
                            <div className="mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
                                <div>
                                    <p className={`text-[7px] font-bold uppercase tracking-[0.2em] mb-0.5 inline-block px-2 py-1.5 rounded-lg
                                        ${isDarkMode ? "text-black bg-white" : "text-white bg-blue-600"}`}>
                                        B2B · New Booking
                                    </p>
                                    <h1 className={`text-sm md:text-base font-bold tracking-tight ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                        Create Forward Shipment
                                    </h1>
                                    <p className={`text-[8px] ${isDarkMode ? "text-gray-600" : "text-gray-600"}`}>
                                        Fill in customer, order, box and invoice details to generate a new B2B shipment.
                                    </p>
                                </div>

                                <div className="flex flex-col md:flex-row gap-1.5 md:shrink-0">
                                    <button
                                        onClick={handleNaviagtion}
                                        className={`w-full md:w-auto px-3 py-1.5 rounded-lg flex items-center justify-center font-semibold text-[9px] border transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 shadow-sm
                                            ${isDarkMode
                                                ? "bg-white text-black border-gray-600 hover:bg-gray-200"
                                                : "bg-blue-600 text-white border-gray-300 hover:bg-gray-700"
                                            }`}
                                    >
                                        <IoMdArrowRoundBack className="mr-1.5" size={10} />
                                        Create Forward Order
                                    </button>

                                    <button
                                        onClick={UploadBulkViewClick}
                                        className={`w-full md:w-auto px-3 py-1.5 rounded-lg flex items-center justify-center font-semibold text-[9px] border transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                                            ${isDarkMode
                                                ? "bg-gray-800/50 border-gray-700 text-white hover:bg-gray-800"
                                                : "bg-gray-100 border-gray-600 text-gray-700 hover:bg-gray-200"
                                            }`}
                                    >
                                        <FaBox className="mr-1.5" size={10} />
                                        Upload Bulk
                                    </button>
                                </div>
                            </div>

                            <B2BUploadBulk
                                isopen={uploadBulkPopupOpen}
                                onClose={handleCloseUploadBulk}
                            />

                            <form onSubmit={handleSubmit(onSubmit, onErrors)}>
                                {/* ================= CUSTOMER DETAILS ================= */}
                                <SectionCard number="01" icon={FaUser} title="Customer Details" subtitle="Who is this shipment going to" className="mb-2.5" isDarkMode={isDarkMode}>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        {/* Row 1: Warehouse (Searchable), First Name, Last Name */}
                                        <div>
                                            <label className={labelBase}>Warehouse</label>
                                            <SearchableWarehouseSelect
                                                value={wareHouseId}
                                                onChange={handleWarehouseSelect}
                                                options={wareHouseList}
                                                isDarkMode={isDarkMode}
                                                error={errors.warehouse?.message}
                                                placeholder="Search warehouse..."
                                            />
                                        </div>

                                        <Field icon={FaUser} label="First Name" htmlFor="firstName" error={errors.firstName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="firstName"
                                                {...register("firstName")}
                                                className={`${inputBase} ${errors.firstName ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="Enter first name"
                                            />
                                        </Field>

                                        <Field icon={FaUser} label="Last Name" htmlFor="lastName" error={errors.lastName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="lastName"
                                                {...register("lastName")}
                                                className={`${inputBase} ${errors.lastName ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="Last name"
                                            />
                                        </Field>

                                        {/* Row 2: Email, Mobile */}
                                        <Field icon={FaEnvelope} label="Email" htmlFor="email" isDarkMode={isDarkMode}>
                                            <input
                                                id="email"
                                                {...register("email")}
                                                className={inputBase}
                                                type="email"
                                                placeholder="Enter email"
                                            />
                                        </Field>

                                        <div className="relative">
                                            <label htmlFor="mobile" className={labelBase}>Mobile No.</label>
                                            <div className="relative group">
                                                <FaPhoneAlt
                                                    size={10}
                                                    className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                                                        ${isDarkMode ? "text-gray-500 group-focus-within:text-white" : "text-gray-400 group-focus-within:text-black"}`}
                                                />
                                                <input
                                                    id="mobile"
                                                    type="tel"
                                                    maxLength="10"
                                                    placeholder="Enter 10 digit mobile no."
                                                    {...register("mobile")}
                                                    className={`${inputBase} ${errors.mobile ? '!border-red-500/70' : ''}`}
                                                    onChange={(e) => {
                                                        const phone = e.target.value.replace(/\D/g, "");
                                                        setValue("mobile", phone);
                                                        if (phone.length === 10) {
                                                            fetchByPhone(phone);
                                                        } else {
                                                            setShowDropdown(false);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {errors.mobile && errorText(errors.mobile.message)}
                                            {showDropdown && (
                                                <div
                                                    className={`absolute z-50 w-[350px] max-h-40 overflow-y-auto rounded-md border mt-0.5 shadow-lg
                                                        ${isDarkMode ? "bg-black/95 border-gray-700" : "bg-white border-gray-300"}`}
                                                    onWheel={(e) => e.stopPropagation()}
                                                >
                                                    {phoneResults.map((item, index) => (
                                                        <div
                                                            key={index}
                                                            className={`p-2 text-[9px] cursor-pointer transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                                                            onClick={() => handleSelectCustomer(item)}
                                                        >
                                                            <div className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-800"}`}>{item.consignee_name}</div>
                                                            <div className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
                                                                {item.address}, {item.city} - {item.zip}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* LRN Option */}
                                        <div>
                                            <label className={labelBase}>LRN Option</label>
                                            <div className="flex flex-col gap-0.5">
                                                <div className="flex items-center mt-1 gap-2">
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        <label className="flex items-center gap-1 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="lrnOption"
                                                                value="auto"
                                                                checked={lrnOption === 'auto'}
                                                                onChange={() => setLrnOption('auto')}
                                                                className="w-3 h-3 accent-blue-600"
                                                            />
                                                            <span className={`text-[9px] font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Auto</span>
                                                        </label>
                                                        <label className="flex items-center gap-1 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="lrnOption"
                                                                value="manual"
                                                                checked={lrnOption === 'manual'}
                                                                onChange={() => setLrnOption('manual')}
                                                                className="w-3 h-3 accent-blue-600"
                                                            />
                                                            <span className={`text-[9px] font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Manual</span>
                                                        </label>
                                                    </div>
                                                    {lrnOption === 'manual' && (
                                                        <div className="flex-1">
                                                            <input
                                                                type="text"
                                                                name="manualLrn"
                                                                placeholder="Enter LRN manually"
                                                                value={manualLrn}
                                                                onChange={(e) => setManualLrn(e.target.value)}
                                                                className={inputPlain}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.manualLrn && errorText(errors.manualLrn.message)}
                                            </div>
                                        </div>

                                        {/* Row 3: Address, Pincode, City */}
                                        <Field icon={FaMapMarkerAlt} label="Full Address" htmlFor="address" error={errors.address?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="address"
                                                {...register("address")}
                                                className={`${inputBase} ${errors.address ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                placeholder="Enter full address"
                                            />
                                        </Field>

                                        <Field icon={FaHashtag} label="Pincode" htmlFor="pincode" error={errors.pincode?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="pincode"
                                                maxLength="6"
                                                {...register("pincode")}
                                                className={`${inputBase} ${errors.pincode ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                placeholder="Enter pincode"
                                            />
                                        </Field>

                                        <Field icon={FaCity} label="City" htmlFor="city" error={errors.city?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="city"
                                                {...register("city")}
                                                className={`${inputBase} ${errors.city ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="City"
                                            />
                                        </Field>

                                        {/* Row 4: State */}
                                        <Field icon={FaFlag} label="State" htmlFor="state" error={errors.state?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="state"
                                                {...register("state")}
                                                className={`${inputBase} ${errors.state ? '!border-red-500/70' : ''}`}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="State"
                                            />
                                        </Field>
                                    </div>
                                </SectionCard>

                                <div className="flex flex-col md:flex-row gap-2.5 mb-2.5">
                                    {/* ================= ORDER DETAILS ================= */}
                                    <SectionCard number="02" icon={FaShoppingCart} title="Order Details" subtitle="Payment & product info" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <Field icon={FaHashtag} label="Order ID" htmlFor="orderId" error={errors.orderId?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="orderId"
                                                    {...register("orderId")}
                                                    className={`${inputBase} ${errors.orderId ? '!border-red-500/70' : ''}`}
                                                    type="text"
                                                    placeholder="Enter Order ID"
                                                />
                                            </Field>

                                            <Field icon={FaClipboardList} label="Product Description" htmlFor="productDes" error={errors.productDes?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="productDes"
                                                    {...register("productDes")}
                                                    className={`${inputBase} ${errors.productDes ? '!border-red-500/70' : ''}`}
                                                    type="text"
                                                    placeholder="Product Description"
                                                />
                                            </Field>

                                            <Field icon={FaWallet} label="Payment Mode" htmlFor="paymentMode" error={errors.paymentMode?.message} isDarkMode={isDarkMode}>
                                                <select
                                                    id="paymentMode"
                                                    {...register("paymentMode")}
                                                    className={`${inputBase} ${errors.paymentMode ? '!border-red-500/70' : ''}`}
                                                >
                                                    <option value="prepaid" className={isDarkMode ? "bg-black" : ""}>Prepaid</option>
                                                    <option value="cod" className={isDarkMode ? "bg-black" : ""}>COD</option>
                                                </select>
                                            </Field>

                                            <Field icon={FaRupeeSign} label="Amount" htmlFor="codAmount" error={errors.codAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="codAmount"
                                                    {...register("codAmount")}
                                                    disabled={paymentMode !== "cod"}
                                                    className={`${inputBase} ${errors.codAmount ? '!border-red-500/70' : ''} disabled:opacity-40 disabled:cursor-not-allowed`}
                                                    type="text"
                                                    placeholder="Amount"
                                                />
                                            </Field>
                                        </div>

                                        {/* Insurance Section */}
                                        <div className={`mt-2 p-2 rounded-lg border ${isDarkMode ? "bg-gray-900/40 border-gray-700" : "bg-gray-100/60 border-gray-300"}`}>
                                            <h3 className={`text-[10px] font-semibold flex items-center gap-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                                <FaShieldAlt className="text-[10px]" />
                                                Insure your shipment
                                            </h3>
                                            <p className={`text-[9px] mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                                                Are you sure you want to ship the item at your own risk?
                                            </p>
                                            <div className="mt-1.5 space-y-1">
                                                <label className="flex items-center space-x-1.5 cursor-pointer text-[9px]">
                                                    <input
                                                        type="radio"
                                                        {...register("insurance")}
                                                        value="ownerRisk"
                                                        className="w-3 h-3 accent-blue-600"
                                                    />
                                                    <span className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                                                        Yes,(The charges will be applied as per agreement)
                                                    </span>
                                                </label>
                                                <label className="flex items-center space-x-1.5 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        {...register("insurance")}
                                                        value="carrierRisk"
                                                        className="w-3 h-3 accent-blue-600"
                                                    />
                                                    <span className={`font-medium text-[9px] ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                                                        No,<span className={isDarkMode ? "text-gray-500" : "text-gray-400"}> (The nominal charges will be applied)</span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </SectionCard>

                                    {/* ================= BOX DETAILS ================= */}
                                    <SectionCard number="03" icon={FaBox} title="Box Details" subtitle="Total boxes, weight & dimensions" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="mb-2">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                <Field icon={FaBoxOpen} label="Total Boxes" isDarkMode={isDarkMode}>
                                                    <input
                                                        type="number"
                                                        onChange={handleTotalBoxesChange}
                                                        className={inputBase}
                                                        placeholder="Enter total boxes"
                                                    />
                                                </Field>
                                                <Field icon={FaWeightHanging} label="Total Weight (kg)" isDarkMode={isDarkMode}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        {...register("total_weight")}
                                                        className={inputBase}
                                                        placeholder="Enter total weight"
                                                    />
                                                </Field>
                                                <div>
                                                    <label className={labelBase}>Unit</label>
                                                    <select
                                                        className={inputPlain}
                                                    >
                                                        <option value="cm">Centimeters (cm)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {boxSets.map((boxSet, index) => (
                                            <div key={index} className={`mb-1.5 p-2 rounded-lg border ${isDarkMode ? "bg-gray-900/40 border-gray-700" : "bg-gray-100/60 border-gray-300"}`}>
                                                <div className="grid grid-cols-1 md:grid-cols-5 gap-1.5 mb-1.5">
                                                    <div>
                                                        <label className={labelBase}>Qty</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={remainingBoxes + (boxSet.quantity || 0)}
                                                            value={boxSet.quantity || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'quantity', e.target.value)}
                                                            className={`${inputPlain} !py-1 ${!boxSet.quantity || boxSet.quantity <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>L</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.length || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'length', e.target.value)}
                                                            className={`${inputPlain} !py-1 ${!boxSet.length || parseFloat(boxSet.length) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>W</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.width || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'width', e.target.value)}
                                                            className={`${inputPlain} !py-1 ${!boxSet.width || parseFloat(boxSet.width) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>H</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.height || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'height', e.target.value)}
                                                            className={`${inputPlain} !py-1 ${!boxSet.height || parseFloat(boxSet.height) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="flex items-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteBoxSet(index)}
                                                            className={`p-1 rounded-md transition-colors ${isDarkMode ? "text-gray-400 hover:bg-gray-800 hover:text-white" : "text-gray-500 hover:bg-gray-200 hover:text-black"}`}
                                                            title="Delete box set"
                                                        >
                                                            <FaTrash size="11" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="flex items-center justify-between">
                                            <button
                                                onClick={handleAddBoxSet}
                                                disabled={remainingBoxes === 0}
                                                className={`flex items-center px-2.5 py-1 text-[9px] font-semibold rounded-lg transition-all duration-200 border hover:-translate-y-0.5 active:translate-y-0
                                                    ${remainingBoxes === 0
                                                        ? (isDarkMode ? 'bg-gray-800/40 text-gray-600 border-gray-700 cursor-not-allowed' : 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed')
                                                        : (isDarkMode
                                                            ? 'bg-white text-black border-gray-600 hover:bg-gray-200'
                                                            : 'bg-blue-600 text-white border-gray-300 hover:bg-gray-700')
                                                    }`}
                                            >
                                                <FaPlus className="mr-1 text-[9px]" />
                                                Add Box Set
                                            </button>
                                            {remainingBoxes > 0 && (
                                                <span className={`text-[9px] font-medium ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>Remaining: {remainingBoxes}</span>
                                            )}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="flex flex-col md:flex-row gap-2.5 mb-2.5">
                                    {/* ================= INVOICE DETAILS ================= */}
                                    <SectionCard number="04" icon={FaFileInvoice} title="Invoice Details" subtitle="Invoice number, e-waybill & amount" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                            <Field icon={FaHashtag} label="Invoice Number" htmlFor="invoiceNumber" error={errors.invoiceNumber?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="invoiceNumber"
                                                    {...register("invoiceNumber")}
                                                    className={`${inputBase} ${errors.invoiceNumber ? '!border-red-500/70' : ''}`}
                                                    type="text"
                                                    placeholder="Enter Invoice Number"
                                                />
                                            </Field>
                                            <Field icon={FaHashtag} label="E-WaybillNo" htmlFor="eWaybillNo" error={errors.eWaybillNo?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="eWaybillNo"
                                                    {...register("eWaybillNo")}
                                                    className={`${inputBase} ${errors.eWaybillNo ? '!border-red-500/70' : ''}`}
                                                    type="text"
                                                    placeholder="E-WaybillNo"
                                                />
                                            </Field>
                                            <Field icon={FaRupeeSign} label="Amount" htmlFor="totalAmount" error={errors.totalAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="totalAmount"
                                                    {...register("invoiceAmount")}
                                                    className={`${inputBase} ${errors.invoiceAmount ? '!border-red-500/70' : ''}`}
                                                    type="text"
                                                    placeholder="Total Amount"
                                                />
                                            </Field>
                                        </div>
                                    </SectionCard>
                                </div>

                                {/* SUBMIT */}
                                <div className="flex justify-center mt-3">
                                    <button
                                        type="submit"
                                        className={`group relative w-full md:w-[42%] overflow-hidden py-1.5 font-bold text-[10px] rounded-lg flex items-center justify-center gap-1.5 border hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 shadow-sm
                                            ${isDarkMode
                                                ? "bg-white text-black border-gray-600 hover:bg-gray-200"
                                                : "bg-blue-600 text-white border-gray-300 hover:bg-gray-700"
                                            }`}
                                    >
                                        <span className={`absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent ${isDarkMode ? "via-black/10" : "via-white/20"} to-transparent`} />
                                        <span className="relative flex items-center gap-1.5">
                                            {isSubmitting ? <Spinner /> : "Add order"}
                                            <FaCalculator size={10} />
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <B2BViewRateForword
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={tableData}
                    reqBody={reqBody}
                />
            </div>
        </>
    )
}